import React from 'react';
import { Flag } from 'lucide-react';
import { HOLES, DISTANCES, TOTAL_PAR, totalFor } from './masterplan/courseData';

const GolfParcours = () => {
  // Données issues de la carte de score officielle (départ noir) — même source que le Master Plan
  const holesData = HOLES.map((h) => ({ number: h.number, distance: DISTANCES.noir[h.number - 1], par: h.par }));

  const totalDistance = totalFor('noir').toLocaleString('fr-FR');

  return (
    <section id="parcours" className="py-24 lg:py-32 bg-[#faf8f5] relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="font-montserrat text-[#c9a962] text-xs tracking-[0.4em] uppercase">
            Le Parcours
          </span>
          <div className="w-16 h-px bg-[#c9a962] mx-auto my-6" />
          <h2 className="font-playfair text-4xl lg:text-5xl text-[#1a3c2a] mb-6">
            9 Trous <span className="text-[#c9a962] italic">d'Exception</span>
          </h2>
          <p className="font-montserrat text-base text-gray-500 font-light max-w-2xl mx-auto">
            Un parcours 9 trous conçu par Alain Cnrjac pour offrir une expérience de jeu exigeante et accessible, 
            respectant les standards internationaux USGA.
          </p>
        </div>

        {/* Main Content - New Image Layout */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Images Grid with new images */}
          <div className="grid grid-cols-12 gap-4">
            {/* Main Image - Golfer */}
            <div className="col-span-7">
              <div className="aspect-[3/4] overflow-hidden rounded-lg shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_ab5938d0-e883-4589-ba7b-f375ea61faf7/artifacts/rjigs5oi_parc2.png"
                  alt="Les Terres de Songon Golf Club"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                />
              </div>
            </div>
            
            {/* Side Images */}
            <div className="col-span-5 flex flex-col gap-4 pt-6">
              {/* Masterplan Color */}
              <div className="aspect-square overflow-hidden rounded-lg shadow-lg">
                <img
                  src="https://customer-assets.emergentagent.com/job_ab5938d0-e883-4589-ba7b-f375ea61faf7/artifacts/tmo87t5i_parc1.png"
                  alt="Les Terres de Songon Golf Club"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                />
              </div>
              
              {/* Stats Box */}
              <div className="flex-1 bg-[#1a3c2a] rounded-lg flex items-center justify-center p-5 min-h-[120px]">
                <div className="text-center">
                  <Flag className="w-7 h-7 text-[#c9a962] mx-auto mb-2" />
                  <p className="font-playfair text-2xl text-white">{totalDistance} m</p>
                  <p className="font-montserrat text-white/60 text-[10px] tracking-wider uppercase mt-1">Départ noir</p>
                </div>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="lg:pl-6">
            <h3 className="font-playfair text-2xl lg:text-3xl text-[#1a3c2a] mb-8">
              Caractéristiques <span className="text-[#c9a962] italic">techniques</span>
            </h3>
            
            <div className="grid grid-cols-2 gap-6 mb-8">
              {[
                { value: '9', label: 'Trous', sub: 'Parcours complet' },
                { value: `${totalDistance} m`, label: 'Distance', sub: 'Départ noir' },
                { value: '300m', label: 'Practice', sub: 'Double side' },
                { value: 'USGA', label: 'Normes', sub: 'Standard international' }
              ].map((stat, index) => (
                <div key={index} className="border-l-2 border-[#c9a962]/30 pl-5">
                  <div className="font-playfair text-2xl text-[#c9a962]">{stat.value}</div>
                  <div className="font-montserrat text-sm text-[#1a3c2a] font-medium">{stat.label}</div>
                  <div className="font-montserrat text-xs text-gray-400 mt-0.5">{stat.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>


        {/* Holes Details Table */}
        <div className="bg-white rounded-xl shadow-lg border border-[#c9a962]/10 overflow-hidden mb-16">
          <div className="bg-[#1a3c2a] px-6 py-3">
            <h3 className="font-playfair text-lg text-white">
              Détail des <span className="text-[#c9a962] italic">9 trous</span>
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#c9a962]/20">
                  <th className="font-montserrat text-[#1a3c2a] text-xs uppercase tracking-wider py-3 px-4 text-left">Trou</th>
                  {holesData.map((hole) => (
                    <th key={hole.number} className="font-montserrat text-[#c9a962] text-sm py-3 px-2 text-center font-semibold">
                      N°{hole.number}
                    </th>
                  ))}
                  <th className="font-montserrat text-[#1a3c2a] text-xs uppercase tracking-wider py-3 px-4 text-center bg-[#c9a962]/10">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="font-montserrat text-gray-600 text-sm py-3 px-4">Distance</td>
                  {holesData.map((hole) => (
                    <td key={hole.number} className="font-montserrat text-[#1a3c2a] text-sm py-3 px-2 text-center">
                      {hole.distance}m
                    </td>
                  ))}
                  <td className="font-montserrat text-[#c9a962] text-sm py-3 px-4 text-center font-semibold bg-[#c9a962]/10">
                    {totalDistance} m
                  </td>
                </tr>
                <tr className="border-t border-[#c9a962]/10">
                  <td className="font-montserrat text-gray-600 text-sm py-3 px-4">Par</td>
                  {holesData.map((hole) => (
                    <td key={hole.number} className="font-montserrat text-[#1a3c2a] text-sm py-3 px-2 text-center">
                      {hole.par}
                    </td>
                  ))}
                  <td className="font-montserrat text-[#c9a962] text-sm py-3 px-4 text-center font-semibold bg-[#c9a962]/10">
                    {TOTAL_PAR}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </section>
  );
};

export default GolfParcours;
